import { useState, useEffect } from 'react';
import { apiRequest } from '../../platform/api';
import { Zap, MessageSquare, FileSearch, Loader2, Check, Info } from 'lucide-react';

interface ModelSetting {
  model: string;
  max_tokens: number;
}

interface ModelSettings {
  model_extraction: ModelSetting;
  model_post_generation: ModelSetting;
  model_voice_generation: ModelSetting;
}

// Available models - ordered by speed/cost
const AVAILABLE_MODELS = [
  // OpenAI (fastest, cheapest)
  { value: 'openai/gpt-4o-mini', label: 'GPT-4o Mini', provider: 'OpenAI', speed: 'Fastest', cost: 'Cheapest' },

  // Claude Haiku (fast, affordable)
  { value: 'anthropic/claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5', provider: 'Anthropic', speed: 'Fast', cost: 'Low' },

  // Claude Sonnet (balanced)
  { value: 'anthropic/claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5', provider: 'Anthropic', speed: 'Medium', cost: 'Medium' },
  { value: 'anthropic/claude-sonnet-4-6', label: 'Claude Sonnet 4.6', provider: 'Anthropic', speed: 'Medium', cost: 'Medium' },

  // Claude Opus (powerful, expensive)
  { value: 'anthropic/claude-opus-4-6', label: 'Claude Opus 4.6', provider: 'Anthropic', speed: 'Slow', cost: 'High' },
  { value: 'anthropic/claude-opus-4-6-20250514', label: 'Claude Opus 4.6 (dated)', provider: 'Anthropic', speed: 'Slow', cost: 'High' },
];

const MAX_TOKENS_PRESETS = [
  { value: 1024, label: '1K - Very Short' },
  { value: 2048, label: '2K - Short' },
  { value: 4096, label: '4K - Standard' },
  { value: 8192, label: '8K - Long' },
];

export function AdminModelSettingsPage() {
  const [settings, setSettings] = useState<ModelSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const result = await apiRequest<{ data: ModelSettings }>('/api/admin/settings/models');
      setSettings(result.data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;

    try {
      setSaving(true);
      setError(null);
      await apiRequest('/api/admin/settings/models', {
        method: 'PATCH',
        body: settings,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key: keyof ModelSettings, field: keyof ModelSetting, value: string | number) => {
    if (!settings) return;
    setSettings({
      ...settings,
      [key]: {
        ...settings[key],
        [field]: value,
      },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!settings) return null;

  const settingConfigs = [
    {
      key: 'model_extraction' as keyof ModelSettings,
      title: 'Knowledge Extraction',
      description: 'Model used to extract insights from your sources (notes, documents, meetings)',
      icon: FileSearch,
      color: 'blue',
      recommendation: 'Use GPT-4o Mini or Claude Haiku for fast, cost-effective extraction',
    },
    {
      key: 'model_post_generation' as keyof ModelSettings,
      title: 'Post Generation',
      description: 'Model used to write LinkedIn posts from your knowledge base',
      icon: MessageSquare,
      color: 'green',
      recommendation: 'Use Claude Sonnet for high-quality, engaging content',
    },
    {
      key: 'model_voice_generation' as keyof ModelSettings,
      title: 'Voice Profile',
      description: 'Model used to analyze writing samples and create your unique voice profile',
      icon: Zap,
      color: 'purple',
      recommendation: 'Use Claude Sonnet for accurate voice analysis',
    },
  ];

  return (
    <div className="p-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Model Configuration</h1>
        <p className="text-gray-500 mt-2">Configure which AI models to use for different operations</p>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Success */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
          <Check className="h-4 w-4" />
          Settings saved successfully! Changes will take effect immediately.
        </div>
      )}

      {/* Info Banner */}
      <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <div className="font-medium mb-1">Optimization Tip</div>
            <div className="text-blue-700">
              For fastest extraction: Use GPT-4o Mini with 2K tokens. For highest quality posts: Use Claude Sonnet 4.5 with 4K tokens.
              Changes apply immediately to new generations.
            </div>
          </div>
        </div>
      </div>

      {/* Settings Cards */}
      <div className="space-y-6">
        {settingConfigs.map((config) => {
          const Icon = config.icon;
          const setting = settings[config.key];

          return (
            <div key={config.key} className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className={`p-3 bg-${config.color}-100 rounded-lg`}>
                  <Icon className={`h-6 w-6 text-${config.color}-600`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{config.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">{config.description}</p>
                  <p className="text-xs text-gray-400 mt-2 italic">{config.recommendation}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Model Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                  <select
                    value={setting.model}
                    onChange={(e) => updateSetting(config.key, 'model', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    {AVAILABLE_MODELS.map((model) => (
                      <option key={model.value} value={model.value}>
                        {model.label} - {model.speed}, {model.cost} cost
                      </option>
                    ))}
                  </select>
                  <div className="mt-1 text-xs text-gray-400">
                    Current: {AVAILABLE_MODELS.find(m => m.value === setting.model)?.provider || 'Unknown'}
                  </div>
                </div>

                {/* Max Tokens */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max Output Tokens</label>
                  <select
                    value={setting.max_tokens}
                    onChange={(e) => updateSetting(config.key, 'max_tokens', Number(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    {MAX_TOKENS_PRESETS.map((preset) => (
                      <option key={preset.value} value={preset.value}>
                        {preset.label}
                      </option>
                    ))}
                  </select>
                  <div className="mt-1 text-xs text-gray-400">
                    Lower = faster & cheaper, Higher = more detailed output
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Save Button */}
      <div className="mt-8 flex justify-end gap-3">
        <button
          onClick={loadSettings}
          className="px-6 py-3 text-gray-600 hover:text-gray-900 font-medium"
        >
          Reset to Current
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check className="h-4 w-4" />
              Save Settings
            </>
          )}
        </button>
      </div>

      {/* Cost Impact Warning */}
      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-yellow-900">
            <div className="font-medium mb-1">Cost Impact</div>
            <div className="text-yellow-700">
              Choosing faster models (GPT-4o Mini, Claude Haiku) will significantly reduce costs.
              Claude Opus is 15-30x more expensive than Haiku/GPT-4o-mini.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminModelSettingsPage;
